import { useQueryClient } from "@tanstack/react-query";

import {
  type Encounter,
  getGetEncounterByIdQueryKey,
  getListActionItemsForEncounterQueryKey,
  getListAgendaItemsQueryKey,
  getListMedicalCodesByEncounterQueryKey,
} from "@elation/api-client/src/stitch-web";

import { useAppBusEvent } from "@src/features/sse";

import { matchesActiveEncounter } from "../domain";
import {
  useEncounterCompletedAtBaseline,
  useEncounterId,
  useIsEncounterCompleted,
  useIsEncounterFinalizing,
  useIsEncounterFinishRequested,
  useIsEncounterNoteGenerationActive,
} from "./useEncounterActorSelectors";

type CachedEncounter = { response?: unknown; data: Encounter };

export function useEncounter() {
  const queryClient = useQueryClient();

  const encounterId = useEncounterId();
  const completedAtBaseline = useEncounterCompletedAtBaseline();
  const isEncounterCompleted = useIsEncounterCompleted();
  const isEncounterFinalizing = useIsEncounterFinalizing();
  const isEncounterFinishRequested = useIsEncounterFinishRequested();
  const isEncounterNoteGenerationActive = useIsEncounterNoteGenerationActive();

  useAppBusEvent("encounter.finalizing", (payload) => {
    queryClient.setQueryData<CachedEncounter>(
      getGetEncounterByIdQueryKey(payload.encounterId),
      (old) =>
        old?.data
          ? { ...old, data: { ...old.data, status: "finalizing" } }
          : old,
    );
  });

  useAppBusEvent("encounter.completed", (payload) => {
    queryClient.setQueryData<CachedEncounter>(
      getGetEncounterByIdQueryKey(payload.encounterId),
      (old) =>
        old?.data
          ? { ...old, data: { ...old.data, status: "completed" } }
          : old,
    );

    Promise.all([
      queryClient.invalidateQueries({
        queryKey: getGetEncounterByIdQueryKey(payload.encounterId),
      }),
      queryClient.invalidateQueries({
        queryKey: getListActionItemsForEncounterQueryKey(payload.encounterId),
      }),
      queryClient.invalidateQueries({
        queryKey: getListMedicalCodesByEncounterQueryKey(payload.encounterId),
      }),
    ]).catch((err) =>
      console.error(
        "[useEncounter] Failed to invalidate queries after encounter.completed",
        {
          encounterId: payload.encounterId,
          err,
        },
      ),
    );
  });

  useAppBusEvent("encounter.coding_updated", (payload) => {
    if (!matchesActiveEncounter(payload.encounterId)) return;

    queryClient
      .invalidateQueries({
        queryKey: getListMedicalCodesByEncounterQueryKey(payload.encounterId),
      })
      .catch((err) =>
        console.error(
          "[useEncounter] Failed to invalidate medical codes after encounter.coding_updated",
          { encounterId: payload.encounterId, err },
        ),
      );
  });

  useAppBusEvent("encounter.reopened", (payload) => {
    if (!matchesActiveEncounter(payload.encounterId)) return;

    queryClient.setQueryData<CachedEncounter>(
      getGetEncounterByIdQueryKey(payload.encounterId),
      (old) =>
        old?.data
          ? { ...old, data: { ...old.data, status: "in_progress" } }
          : old,
    );

    Promise.all([
      queryClient.invalidateQueries({
        queryKey: getGetEncounterByIdQueryKey(payload.encounterId),
      }),
      queryClient.invalidateQueries({
        queryKey: getListAgendaItemsQueryKey(payload.encounterId),
      }),
    ]).catch((err) =>
      console.error(
        "[useEncounter] Failed to invalidate queries after encounter.reopened",
        {
          encounterId: payload.encounterId,
          err,
        },
      ),
    );
  });

  return {
    encounterId,
    completedAtBaseline,
    isEncounterCompleted,
    isEncounterFinalizing,
    isEncounterFinishRequested,
    isEncounterNoteGenerationActive,
  };
}
